import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "@/lib/router-compat";
import { ExternalLink, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import type { UnifiedResource } from "@/lib/resourceTypes";
import { getContentTypeMeta } from "@/lib/resourceTypes";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { localizeContentRow } from "@/lib/localizeContent";
import { uiT } from "@/i18n/ui";

const BLOG_SLUGS = [
  "ivd-clinical-trials-integration",
  "ivd-use-in-eu-clinical-trials",
  "annex-xiv-common-delays",
];

const EXTERNAL_TOOLS = [
  {
    title: "IVDR Performance Studies Navigator",
    summary: "Interactive tool to help you navigate IVDR performance study requirements and determine the right path for your IVD device.",
    url: "https://ivdr-performance-studies.lovable.app",
  },
  {
    title: "CDx Regulatory Readiness Assessment",
    summary: "Evaluate your companion diagnostic's regulatory readiness across key dimensions and get actionable recommendations.",
    url: "https://cdx-readiness.qbdgroup.com",
  },
];

const CdxWebinarRelatedContent = () => {
  const lang = useLanguage();
  const t = uiT(lang);
  const { data: blogs = [] } = useQuery<UnifiedResource[]>({
    queryKey: ["cdx-webinar-related-blogs", lang],
    queryFn: async () => {
      // Fetch EN originals
      const { data: enData, error } = await supabase
        .from("blog_posts")
        .select("id, slug, title, summary, featured_image, category, published_at, author, tags, content")
        .not("published_at", "is", null)
        .in("slug", BLOG_SLUGS);
      if (error) throw error;
      const enPosts = enData ?? [];

      if (lang === "es" && enPosts.length > 0) {
        // Fetch Spanish translations linked to these posts
        const enIds = enPosts.map((p) => p.id);
        const { data: esPosts } = await supabase
          .from("blog_posts")
          .select("id, slug, title, summary, featured_image, category, published_at, author, tags, content, translation_of")
          .eq("lang", "es")
          .in("translation_of", enIds);
        const esMap = new Map((esPosts ?? []).map((p) => [p.translation_of, p]));
        // Merge: use Spanish version if available, else fall back to English
        const merged = enPosts.map((en) => esMap.get(en.id) ?? en);
        const map = new Map(enPosts.map((d) => [d.slug, merged[enPosts.indexOf(d)]]));
        return BLOG_SLUGS
          .map((s) => map.get(s))
          .filter(Boolean)
          .map((item: any) => ({ ...item, tags: [], contentType: "blog_posts" as const }));
      }

      const map = new Map(enPosts.map((d) => [d.slug, d]));
      return BLOG_SLUGS
        .map((s) => map.get(s))
        .filter(Boolean)
        .map((item: any) => ({ ...item, tags: [], contentType: "blog_posts" as const }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const blogMeta = getContentTypeMeta("blog_posts");

  return (
    <section className="py-20 sm:py-28 bg-secondary">
      <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-accent mb-2">{t("keepReading")}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{t("relatedContent")}</h2>
          </div>
          <Link
            to={localizeHref("/en/knowledge-center", lang)}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            {t("viewAll")} <ArrowRight size={13} />
          </Link>
        </div>

        <Carousel opts={{ align: "start", loop: false }} className="w-full">
          <CarouselContent className="-ml-5">
            {blogs.map((post) => (
              <CarouselItem key={post.id} className="pl-5 basis-[280px] sm:basis-[320px]">
                <Link to={localizeHref(`${blogMeta.route}/${post.slug}`, lang)} className="group block">
                  {post.featured_image && (
                    <div className="aspect-[3/2] rounded-xl overflow-hidden bg-muted mb-3">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {t("blog")}
                  </span>
                  <h3 className="text-sm font-semibold text-foreground mt-1 line-clamp-2 group-hover:text-primary transition-colors duration-200">
                    {post.title}
                  </h3>
                </Link>
              </CarouselItem>
            ))}

            {EXTERNAL_TOOLS.map((tool) => (
              <CarouselItem key={tool.url} className="pl-5 basis-[280px] sm:basis-[320px]">
                <a href={tool.url} target="_blank" rel="noopener noreferrer" className="group block">
                  <div className="aspect-[3/2] rounded-xl overflow-hidden bg-accent/10 border border-accent/20 mb-3 flex items-center justify-center">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md text-white bg-accent/90">
                      {t("freeTool")}
                    </span>
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {t("interactiveTool")}
                  </span>
                  <h3 className="text-sm font-semibold text-foreground mt-1 line-clamp-2 group-hover:text-primary transition-colors duration-200">
                    {tool.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tool.summary}</p>
                </a>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-end gap-2 mt-6">
            <CarouselPrevious className="static translate-y-0 h-8 w-8" />
            <CarouselNext className="static translate-y-0 h-8 w-8" />
          </div>
        </Carousel>
      </div>
    </section>
  );
};

export default CdxWebinarRelatedContent;
